'use client';

import { useCallback } from 'react';
import {
	getTokenAddressForDepositAndWithdraw,
	UIMarket,
} from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';

interface WithdrawInsuranceFundAssetsOptions {
	marketIndex: number;
}

export const useWithdrawInsuranceFundAssets = () => {
	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const wallet = useVelocityStore((s) => s.wallet.current.adapter);
	const getTxParams = useGetTxParams();

	const withdrawInsuranceFundUnstakedAssets = useCallback(
		async (opts: WithdrawInsuranceFundAssetsOptions): Promise<boolean> => {
			if (!velocityClient || !wallet?.publicKey) return false;

			const spotMarketConfig = UIMarket.spotMarkets.find(
				(m) => m.marketIndex === opts.marketIndex
			);
			const toastId = `withdraw-unstaked-assets-${opts.marketIndex}`;

			notify({
				id: toastId,
				type: 'awaiting_wallet_confirmation',
				showUntilCancelled: true,
			});

			try {
				const authority = wallet.publicKey;

				const spotMarketAccount = velocityClient.getSpotMarketAccount(
					spotMarketConfig?.marketIndex ?? 0
				);

				if (!spotMarketAccount) {
					throw new Error('Spot market account not found');
				}

				const tokenAccount = await getTokenAddressForDepositAndWithdraw(
					spotMarketAccount,
					authority
				);

				const _txid = await velocityClient?.removeInsuranceFundStake(
					opts.marketIndex,
					tokenAccount,
					getTxParams()
				);

				notify({
					id: toastId,
					type: 'success',
					message: 'Withdrew unstaked.',
					showUntilCancelled: false,
				});

				return true;
			} catch (err: any) {
				console.log(err);
				notify({
					id: toastId,
					type: 'error',
					message: 'Failed to withdraw.',
					showUntilCancelled: false,
				});
				return false;
			}
		},
		[velocityClient, wallet, getTxParams]
	);

	return { withdrawInsuranceFundUnstakedAssets };
};
